import { replaceCms } from "apps/commons/functions/replaceCms";
import type { PropertyData } from "libs/codegen/property-onboarding";
import type { FunctionComponent } from "react";
import { useWatch } from "react-hook-form";
import { AccommodationCategoryEnum } from "../../constants/accommodationCategoryEnum";
import { TextField } from "../../form/TextField";
import { useMetadataAtomValue } from "../../metadataAtom";
import { useStaticDataAtomValue } from "../../staticDataAtom";
import { useAccommodationCategory } from "../../useAccommodationCategory";

interface Props {
    label: string;
    dataElementName?: string;
}

export const NightlyRateController: FunctionComponent<Props> = ({ label, dataElementName }) => {
    const countryId = useWatch<PropertyData>({ name: "country.id" }) as number;
    const {
        countriesList,
        cmsData: {
            pricing: { currencyPerNight, pleaseEnterANightlyRateToContinue, mustBeMoreThan, mustBeLessThan },
        },
    } = useStaticDataAtomValue();

    const {
        experimentsAtom: { isConvergenceRooms },
    } = useMetadataAtomValue();

    const { hasAccommodationTypes } = useAccommodationCategory();

    const country = countriesList.find((item) => item.id === countryId);

    // Check if we should use BasePrice from first room instead of nightlyRate
    const shouldUseRoomBasePrice =
        isConvergenceRooms &&
        !hasAccommodationTypes(AccommodationCategoryEnum.MultipleNha, AccommodationCategoryEnum.Hotel);

    const fieldName = shouldUseRoomBasePrice ? "rooms.0.basePrice" : "nightlyRate";

    return (
        <TextField
            label={label}
            field={fieldName}
            type="number"
            suffix={replaceCms(currencyPerNight, country.currencyName ?? "")}
            // @ts-expect-error
            defaultValue=""
            rules={{
                min: {
                    value: country.minimumRate,
                    message: replaceCms(mustBeMoreThan, country.minimumRate, country.currencyName),
                },
                validate: {
                    notEmpty: (v) => v > 0 || pleaseEnterANightlyRateToContinue,
                },
                ...(country.maximumRate > 0 && {
                    max: {
                        value: country.maximumRate,
                        message: replaceCms(mustBeLessThan, country.maximumRate, country.currencyName),
                    },
                }),
            }}
            dataElementName={dataElementName}
        />
    );
};
